import Vue from 'vue';
import Component from 'vue-class-component';
import _ from 'lodash';
import { ValidationRule } from '@cnamts/vue-dot/src/rules/types';
import { requiredFn } from '@cnamts/vue-dot/src/rules/required';
import { notAfterTodayFn } from '@cnamts/vue-dot/src/rules/notAfterToday';
import { notBeforeDate } from '@/rules/notBeforeDate';
import { isDateValidFn } from '@cnamts/vue-dot/src/rules/isDateValid';
import { hasTextTwoLength } from '@/rules/hasTextLength';
import { hasTextLengthMin } from '@/rules/hasTextLengthMin';
import hasTextLengthMax from '@/rules/hasTextLengthMax';
import { formatDateISO } from '@/services/dateFormatter';
import {
	finessControl, numericControl, alphanumericControl, finessRegExp, finessRegExpNoStars, numeroLotRegExp,
	nameParamDelegationRegExp, codeScolaireRegExp, codeScolaireControl } from '@/services/textInputControl';
import dayjs from 'dayjs';
import { stades } from '@/components/Vaccination/InformationsVaccin/types';
import { luhn } from '@/rules/luhn';

const DATE_NON_VALIDE = 'La date n’est pas valide.';
const CHAMP_NON_VALIDE = 'Ce champ n’est pas valide.';
const CHAMP_OBLIGATOIRE = 'Ce champ est obligatoire.';

@Component
class MixinVaccinationRules extends Vue {

	// activite la validationdes champs lors de la transmission
	allowValidation = false;
	// Init listes choix sans referentiels
	listZones = ['Bras gauche', 'Bras droit', 'Autre'];
	listStades = stades;

	// Date du jour au format ISO
	today: string = formatDateISO(dayjs());
	mask = '##:##';

	numFINESSMaxLength = 14;
	numFINESSMinLength = 9;

	codeSCOLAIREMaxLength = 8;

	finessControl = finessControl;

	codeScolaireControl = codeScolaireControl;

	// La date de saisie minimale
	limitMinDate: dayjs.Dayjs = dayjs('2020-12-27');

	defaultErrorMessages = {
		default: 'La date est hors période autorisée.'
	};

	// Régle de validation du datepicker (génération dynamique)
	datePickerRules: ValidationRule[] = [];
	// Génération dynamique des régles de validation du datepicker

	ruleStade: Array<((value: object) => boolean | string)> = [];
	ruleVaccin: Array<((value: object) => boolean | string)> = [];
	ruleLieu: Array<((value: object) => boolean | string)> = [];
	simpleFinessRules: ValidationRule[] = [
		hasTextTwoLength(9, 14, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && finessRegExp().test(saisie) || CHAMP_NON_VALIDE
	];
	finessRulesWithLuhn: ValidationRule[] = [
		hasTextTwoLength(9, 14, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && finessRegExp().test(saisie) || CHAMP_NON_VALIDE,
		luhn()
	];
	finessRulesNoStars: ValidationRule[] = [
		hasTextTwoLength(9, 14, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && finessRegExpNoStars().test(saisie) || CHAMP_NON_VALIDE,
		luhn()
	];

	codePostalRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		(value) => {
			const id = _.get(value, 'codePostalId');
			return (_.isInteger(id) && id > 0) || CHAMP_NON_VALIDE;
		}
	];

	codeScolaireRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		(value) => {
			const id = _.get(value, 'etablissementId');
			return (_.isInteger(id) && id > 0) || CHAMP_NON_VALIDE;
		}
	];
	centreVaccinationRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		(value) => {
			const id = _.get(value, 'lieuId');
			return (_.isInteger(id) && id > 0) || CHAMP_NON_VALIDE;
		}
	];
	requiredfield: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE })
	];
	lotVaccinRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		hasTextLengthMax(30, { default: CHAMP_NON_VALIDE })
	];
	autreLotVaccinRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		hasTextLengthMax(30, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && numeroLotRegExp().test(saisie.toUpperCase()) || CHAMP_NON_VALIDE
	];

	nameParamDelegationRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		hasTextLengthMax(80, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && nameParamDelegationRegExp().test(saisie.toUpperCase()) || CHAMP_NON_VALIDE
	];

	notRequiredNameRules: ValidationRule[] = [
		hasTextLengthMax(80, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && nameParamDelegationRegExp().test(saisie.toUpperCase()) || CHAMP_NON_VALIDE
	];

	heureRules: ValidationRule[] = [
		requiredFn({ default: CHAMP_OBLIGATOIRE }),
		hasTextLengthMin(4, { default: CHAMP_NON_VALIDE }),
		(saisie) => saisie != null && this.checkSaisie(saisie) || CHAMP_NON_VALIDE
	];
	mailRegex =
		/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/i;
	mailRules = [
		(v: string) => this.mailRegex.test(v) || CHAMP_NON_VALIDE
	];

	numControl = numericControl;
	alphaNumControl = alphanumericControl;

	getDatePickerRules(dateMin: string | null): void {
		this.datePickerRules = [
			requiredFn({
				default: 'Ce champ est obligatoire'
			}),
			isDateValidFn({
				default: DATE_NON_VALIDE,
				wrongFormat: DATE_NON_VALIDE,
				monthNotMatch: '',
				notALeapYear: ''
			}),
			notAfterTodayFn({
				default: DATE_NON_VALIDE
			})
		];
		if (!_.isNil(dateMin)) {
			this.datePickerRules.push(
				notBeforeDate(dateMin, {
					default: DATE_NON_VALIDE
				})
			);
		}
	}
	requiredObject(listObject: object[], comparisonElt: string) {
		// The value can be an array of string in select with the multiple prop
		return (value: object): string | boolean => {
			let isPresent = false;
			_.forEach(listObject, (obj) => {
				isPresent = isPresent || _.isEqual(_.get(value, comparisonElt), _.get(obj, comparisonElt));
			});
			return isPresent || 'Ce champ est obligatoire';
		};
	}

	checkSaisie(saisie: string) {
		const parsing = saisie.match(/(\d{2}):(\d{2})/);
		if (parsing !== null) {
			return parseInt(parsing[1], 10) < 24 && parseInt(parsing[2], 10) < 60;
		}
		return false;
	}
}

export default MixinVaccinationRules;
